import { Download } from 'lucide-react';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type { CostAccountingItem } from '../../types/cost-accounting.js';

/** @graph-connects none */
function yen(amount: number): string {
  return `¥${amount.toLocaleString('ja-JP')}`;
}

/** @graph-connects none */
const EVENT_LABELS: Record<string, string> = { sold: '販売', disposed: '廃棄' };
/** @graph-connects none */
const EVENT_COLORS: Record<string, string> = {
  sold: 'var(--color-accent-primary)',
  disposed: '#b45309',
};

/** @graph-connects none */
function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, { bg: string; color: string }> = {
    STOCKING: { bg: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)' },
    SHIPPED: { bg: '#e8f0fa', color: '#2563a8' },
    SOLD: { bg: '#ecfdf5', color: '#0f766e' },
    DISPOSED: { bg: '#fef3c7', color: '#b45309' },
    MISSING: { bg: '#fff1f2', color: '#be123c' },
    MAINTENANCE: { bg: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)' },
  };
  const style = colorMap[status] ?? {
    bg: 'var(--color-bg-subtle)',
    color: 'var(--color-text-muted)',
  };
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[0.625rem] font-semibold"
      style={{ background: style.bg, color: style.color }}
    >
      {status}
    </span>
  );
}

/** @graph-connects none */
const TH =
  'border-b border-[var(--color-border)] px-3 py-2 text-[0.625rem] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business Item detail table and CSV download for cost accounting. Shows per-item depreciation, book value, status, and event details.
 * @graph-connects none
 */
export function CostAccountingItemDetail({
  items,
  unknownCount,
  onCsvDownload,
}: {
  items: CostAccountingItem[];
  unknownCount: number;
  onCsvDownload: () => void;
}) {
  return (
    <div className="mt-8 border-t border-[var(--color-border)] pt-6">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">個品明細</span>
        <div className="flex items-center gap-3">
          <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
            {items.length.toLocaleString('ja-JP')} 件（うち原価不明 {unknownCount} 件）
          </span>
          <Button variant="secondary" onClick={onCsvDownload}>
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
            明細CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[var(--color-border)]">
        <table className="min-w-full border-collapse text-right text-xs tabular-nums">
          <thead>
            <tr className="bg-[var(--color-bg-subtle,var(--color-bg-base))]">
              <th className={`${TH} text-left`}>個品ID</th>
              <th className={`${TH} text-left`}>コード</th>
              <th className={TH}>取得原価</th>
              <th className={TH}>累計償却</th>
              <th className={TH}>当月償却</th>
              <th className={TH}>簿価</th>
              <th className={TH}>入荷日</th>
              <th className={`${TH} text-center`}>ステータス</th>
              <th className={`${TH} text-center`}>イベント</th>
              <th className={TH}>イベント日</th>
              <th className={TH}>計上額</th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 100).map((item) => (
              <tr key={item.itemId} className="hover:bg-[var(--color-bg-base)]">
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 text-left font-mono">
                  {item.itemId}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 text-left font-mono">
                  {item.itemCode}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5">
                  {yen(item.acquisitionCost)}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5">
                  {yen(item.cumulativeDepreciation)}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5">
                  {yen(item.monthlyDepreciation)}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5">
                  {yen(item.bookValue)}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 font-mono">
                  {item.arrivalDate}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 text-center">
                  <StatusBadge status={item.status} />
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 text-center">
                  {item.eventType ? (
                    <span style={{ color: EVENT_COLORS[item.eventType] }}>
                      {EVENT_LABELS[item.eventType]}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 font-mono">
                  {item.eventDate ?? '—'}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5">
                  {item.eventCost !== null ? (
                    <span style={{ color: EVENT_COLORS[item.eventType ?? ''] }}>
                      {yen(item.eventCost)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length > 100 && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          表示: 100 / {items.length.toLocaleString('ja-JP')} 件（全件はCSVで取得）
        </p>
      )}
    </div>
  );
}
